import { Circle, Loader2, Trash } from "lucide-react"
import { toast } from "sonner"
import { useTask } from "./hook/use-task"
import { useQueryClient } from "@tanstack/react-query"
import { tasksKeys } from "../../api/tasks/keys"
import { Button } from "@workspace/web-ui/components/button"
import { useSendInvoiceMutation } from "../../pages/encounters/api/mutations"
import { useParams } from "@tanstack/react-router"
// import { Button } from "@workspace/web-ui/components/button"

interface TaskCardProps {
  id: string
  title: string
  encounterId: string
  status: string
  date: string
  is_erx_sent?: boolean
  instructions?: string;
  medications: Array<{
    cost: number;
    dosage: string;
    duration: string;
    medication_id: string;
    name: string;
    vitamin: {
      name: string
    };
    refills: number;
    quantity: number;
    instructions: string;
  }>
  onTaskDeleted?: () => void
  patientId: string
  prescriptionId: string
  pharmacy?: {
    fulfillment_type: string;
    pharmacy: {
      address: string;
      name: string;
      phone: string
    }
  }
}

export function TaskCard({ id, encounterId, status, instructions, date, title, onTaskDeleted, medications, patientId, prescriptionId, pharmacy, is_erx_sent }: TaskCardProps) {
  const queryClient = useQueryClient()
  const { onDeleteTask, isDeleting } = useTask({
    id: encounterId,
    taskId: id
  })
  const {program} = useParams({strict:false})
  const sendInvoiceMutation = useSendInvoiceMutation();

  const handleSendInvoice = async () => {
    try {
      await sendInvoiceMutation.mutateAsync({ patient_id: patientId, type_id: prescriptionId, type: "Prescription" });
    } catch (error) {
      console.error("Error sending invoice:", error);
    }
  }
  const handleDelete = async () => {
    try {
      await onDeleteTask();

      toast.success('Task deleted successfully', {
        style: {
          background: "#10B981",
          color: "#FFFFFF",
          borderColor: "#10B981",
          '& svg': {
            color: '#FFFFFF'
          }
        } as React.CSSProperties
      });

      queryClient.invalidateQueries({ queryKey: tasksKeys.getTasksByEncounterId(encounterId) });

      if (onTaskDeleted) onTaskDeleted();

      return;
    } catch (error) {
      console.error("Error deleting task:", error);
      return toast.error(
        'Failed to delete the task.',
        {
          style: {
            background: '#DC2626',
            color: '#FFFFFF',
            borderColor: '#DC2626',
            '& svg': {
              color: '#FFFFFF'
            }
          } as React.CSSProperties
        }
      );
    }
  }

  // const handleSendToPharmacy = () => {
  //     console.log("Send to Pharmacy")
  // }

  // const text = status === 'pending' ? 'Pending' : status === 'approved' ? 'Approved' : 'Rejected'
  return title === "Refill" ? (
    <div className="bg-[#F1F4F9] rounded-[8px] p-4 gap-4 w-full flex flex-col items-center justify-center sm:p-6">
      <div className="flex flex-row items-center justify-between w-full gap-2 sm:gap-0">
        <div className="w-full flex items-center gap-2 sm:w-auto text-left">
          {status !== 'pending' ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block align-middle">
              <rect width="20" height="20" x="2" y="2" rx="10" fill="#00C781" />
              <path d="M16.8327 8.25L10.8743 14.2083L8.16602 11.5" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <Circle className="size-[20px] text-[#4272DD]" />
          )}
          <span className={`text-[14px] sm:text-[16px] text-nowrap ${status !== 'pending' ? 'text-[#00C781] line-through' : 'text-[#4272DD]'}`}>Pay for medication</span>
        </div>
        {status !== 'pending' ? "" : (
                    <div className="w-full sm:w-auto flex justify-end">
                        <button
                            className="self-center text-white"
                            aria-label="Delete task"
                            onClick={handleDelete}
                        >
                            {isDeleting ? (
                                <Loader2 className="w-6 h-6 animate-spin duration-200" />
                            ) : (
                                <Trash className="w-4 h-4 text-[#363840]" />
                            )}
                        </button>
                    </div>
        )}
      </div>
      <div className="w-full sm:w-[80%] bg-[#FFFFFF] pl-3 pr-3 py-3 sm:pl-5 sm:pr-4 rounded-lg border shadow-sm mb-2 group transition-colors">
        <div className="flex flex-col gap-4">
          {medications.map((medication, idx) => (
            <div
              key={medication.medication_id || idx}
              className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-4 w-full"
            >
              <div className="flex flex-col w-full space-y-2">
                <h3 className="text-gray-700 font-medium text-base break-words">{medication.name}</h3>
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-1 xs:gap-4 w-full">
                    <span className="text-[14px] text-[#202020CC]">Price: {medication.cost}</span>
                    <span className="text-[14px] text-[#202020CC]">Dosage: {medication.dosage}</span>
                  </div>
                  <span className="text-[14px] text-[#202020CC]">Duration: {medication.duration}</span>
                  {medication.vitamin && (
                    <span className="text-[14px] text-[#202020CC]">
                      Vitamin: {medication.vitamin.name ?? "--"}
                    </span>
                  )}
                  {program !== "weight-loss" && <span className="text-[14px] text-[#202020CC]">Refills: {medication.refills}</span>}
                  <span className="text-[14px] text-[#202020CC]">Instructions: {medication.instructions}</span>

                  {status !== 'pending' && pharmacy && (
                    <>
                      {/* <span className="text-[14px] text-[#202020CC]">Pharmacy Type: {pharmacy.fulfillment_type}</span> */}
                      {pharmacy.fulfillment_type === "preferred" && (
                        <div className="flex flex-col gap-2 mt-1">
                          <span className="text-[14px] text-[#202020CC] font-semibold">Pharmacy Details:</span>
                          {pharmacy?.pharmacy?.name && (
                            <span className="text-[14px] text-[#202020CC]">Name: {pharmacy.pharmacy.name}</span>
                          )}
                          {pharmacy?.pharmacy?.address && (
                            <span className="text-[14px] text-[#202020CC]">Address: {pharmacy.pharmacy.address}</span>
                          )}
                          {pharmacy?.pharmacy?.phone && (
                            <span className="text-[14px] text-[#202020CC]">Phone: {pharmacy.pharmacy.phone}</span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between w-full sm:w-[80%]">
        <div className="w-full sm:w-auto text-left">
          <span className="block text-[13px] sm:text-base">Assigned: {date}</span>
        </div>
        <div className="mt-1 w-full sm:w-auto flex justify-start sm:justify-end">
          {status === 'pending' && (
            <span className="inline-block px-2 py-1 bg-transparent text-[#EC980C] text-xs rounded-full border border-[#EC980C] font-normal">
              Pending
            </span>
          )}
          {status === 'approved' && (
            <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full border border-green-300 font-normal">
              Approved
            </span>
          )}
          {status === 'rejected' && (
            <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full border border-red-300 font-normal">
              Rejected
            </span>
          )}
        </div>
      </div>
      <div className="w-full sm:w-[80%]">
        <Button
          className="w-full button-primary !bg-[#4272DD] !text-white !flex !gap-2 !border-none !shadow-none"
          disabled={status === 'pending'}
          onClick={handleSendInvoice}
        >
          {sendInvoiceMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            is_erx_sent ? "Resend to Pharmacy" : "Send to Pharmacy"
          )}
        </Button>
      </div>
    </div>
  ) : (
    <div className="bg-[#F1F4F9] rounded-[8px] p-4 gap-2 w-full flex sm:p-6">
      {status !== 'pending' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block align-middle">
                <rect width="20" height="20" x="2" y="2" rx="10" fill="#00C781" />
                <path d="M16.8327 8.25L10.8743 14.2083L8.16602 11.5" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
      ) : (
        <Circle className="size-[20px] text-[#4272DD]" />
      )}

      <div className="flex flex-col items-center w-full">
        <div className="flex flex-row items-start justify-between w-full gap-2 sm:gap-0">
          <div className="w-full sm:w-auto text-left mb-2">
            <span className={`text-[14px] sm:text-[16px] ${status !== 'pending' ? 'text-[#00C781] line-through' : 'text-[#4272DD]'}`}>Book Appointment</span>
          </div>
          <div className="w-full sm:w-auto flex justify-end">
            <button
              className="self-center text-white"
              aria-label="Delete task"
              onClick={handleDelete}
            >
              {isDeleting ? (
                <Loader2 className="w-6 h-6 animate-spin duration-200" />
              ) : (
                <Trash className="w-4 h-4 text-[#363840]" />
              )}
            </button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-2 sm:gap-0">
          <div className="w-full flex flex-col sm:w-auto sm:text-left">
            <span className="text-[12px] sm:text-[14px] text-[#202020CC]">Instructions:</span>
            <span className="text-[12px] sm:text-[14px] text-[#1A202C] font-medium">{instructions}</span>
          </div>
          <div className="mt-1 w-full sm:w-auto flex justify-start sm:justify-end">
            {status === 'pending' && (
              <span className="inline-block px-2 py-1 bg-transparent text-[#EC980C] text-xs rounded-full border border-[#EC980C] font-normal">
                Pending
              </span>
            )}
            {status === 'approved' && (
              <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full border border-green-300 font-normal">
                Approved
              </span>
            )}
            {status === 'rejected' && (
              <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full border border-red-300 font-normal">
                Rejected
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

}